import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import TripleSpotOverlay from "components/project/FileImgEditor/TripleSpotOverlay";
import { getEditorSize } from "lib/utils/getEditorSize";
import { useImageSize } from "lib/hooks/useImageSize";
import {
  getCreateInspectionResultPref,
  setCreateInspectionResultPref,
} from "lib/utils/inspectionResultGenerationPref";

export default function ElectricalSafetyTripleSpotModal({
  file,
  circles,
  setCircles,
  thermalCaptureTarget,
  tripleSpotCaptureTarget,
  currentThermalImg = null,
  currentPaletteOptionNumber,
  imgChangeLoading = false,
  isSaving = false,
  embedded = false,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const [shouldCreateInspectionResult, setShouldCreateInspectionResult] =
    useState(() => getCreateInspectionResultPref());
  const imageSrc = currentThermalImg || file?.image_thermal;
  const editorSize = useImageSize(imageSrc, getEditorSize(file?.csv_origin, "thermal"));
  const isReadyToSave = circles.every((circle) => Boolean(circle));
  const canSelectCreateInspectionResult = circles.filter(Boolean).length >= 3;
  const effectiveCreateInspectionResult =
    canSelectCreateInspectionResult && shouldCreateInspectionResult;
  const createInspectionResultOptionId = `electrical-create-inspection-result-${
    file?.upload_id ?? "current"
  }`;

  const removeCircle = (targetIndex) => {
    if (isSaving) return;
    setCircles((previous) => {
      const next = [...previous];
      next[targetIndex] = undefined;
      return next;
    });
  };

  const handleCreateInspectionResultChange = (event) => {
    setShouldCreateInspectionResult(
      setCreateInspectionResultPref(event.target.checked),
    );
  };

  const handleSave = () => {
    onSave?.({
      createInspectionResult: effectiveCreateInspectionResult,
    });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSaving) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    if (!embedded) {
      document.body.classList.add("reportEditModalOpen");
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (!embedded) {
        document.body.classList.remove("reportEditModalOpen");
      }
    };
  }, [embedded, isSaving, onClose]);

  const shellClassName = embedded
    ? "reportEditModalInlineShell"
    : "reportEditModalOverlay";
  const modalClassName = `reportEditModal electricalTripleSpotModal${
    embedded ? " is-embedded" : ""
  }`;

  return (
    <div className="reportPanel">
      <div className={shellClassName} role="presentation">
        <div
          className={modalClassName}
          role="dialog"
          aria-modal={embedded ? undefined : "true"}
          aria-labelledby="electrical-triplespot-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="reportEditModalHeader">
            <div className="titleBox">
              <h3 id="electrical-triplespot-modal-title">
                {t("imageFileIndex.electricalSafety.downloadModal.title", {
                  defaultValue: "Triple-Spot Analysis Required",
                })}
              </h3>
              <p>
                {t(
                  "imageFileIndex.electricalSafety.downloadModal.description",
                  {
                    defaultValue:
                      "Place three comparison points on the thermal image before downloading the report.",
                  },
                )}
              </p>
            </div>

            <button
              type="button"
              className="iconBtn"
              onClick={onClose}
              disabled={isSaving}
              aria-label={t(
                "imageFileIndex.electricalSafety.downloadModal.closeAria",
                {
                  defaultValue: "Close triple-spot modal",
                },
              )}
            >
              X
            </button>
          </div>

          <div className="reportEditModalBody">
            <label
              className={`inspectionResultGenerationOption${
                canSelectCreateInspectionResult ? "" : " is-disabled"
              }`}
              htmlFor={createInspectionResultOptionId}
            >
              <input
                id={createInspectionResultOptionId}
                type="checkbox"
                checked={effectiveCreateInspectionResult}
                onChange={handleCreateInspectionResultChange}
                disabled={!canSelectCreateInspectionResult || isSaving}
              />
              <span>
                {t(
                  "imageFileIndex.electricalSafety.downloadModal.createInspectionResult",
                  {
                    defaultValue: "점검 결과 생성",
                  },
                )}
              </span>
            </label>

            <div className="electricalTripleSpotModalWorkspace">
              <div className="electricalTripleSpotViewport">
              <div
                ref={tripleSpotCaptureTarget}
                style={{ width: editorSize.width, height: editorSize.height }}
                className="tripleSpotImgBox electricalTripleSpotStage"
              >
                <TripleSpotOverlay
                  file={file}
                  circles={circles}
                  setCircles={setCircles}
                  editorSize={editorSize}
                  disabled={isSaving}
                />
                <img
                  src={imageSrc}
                  style={{
                    display: "block", width: editorSize.width, height: editorSize.height,
                    maxWidth: "none", maxHeight: "none",
                  }}
                  alt={t(
                    "imageFileIndex.electricalSafety.downloadModal.thermalAlt",
                    {
                      defaultValue: "Thermal scan",
                    },
                  )}
                />
              </div>
              </div>

              <div className="electricalTripleSpotModalStatus">
                {circles.map((circle, index) => {
                  const pointLabel = t(
                    "imageFileIndex.electricalSafety.downloadModal.pointLabel",
                    {
                      defaultValue: "L{{index}}",
                      index: index + 1,
                    },
                  );
                  const pointValue = circle?.value
                    ? t(
                        "imageFileIndex.electricalSafety.downloadModal.temperatureValue",
                        {
                          defaultValue: "{{value}} °C",
                          value: circle.value,
                        },
                      )
                    : t(
                        "imageFileIndex.electricalSafety.downloadModal.notSelected",
                        {
                          defaultValue: "Not selected",
                        },
                      );

                  return (
                    <div
                      key={`electrical-circle-status-${index + 1}`}
                      className={circle ? "isComplete" : ""}
                    >
                      <div>
                        <span>{pointLabel}</span>
                        <strong>{pointValue}</strong>
                      </div>
                      <button
                        type="button"
                        className="electricalTripleSpotStatusRemoveButton"
                        onClick={() => removeCircle(index)}
                        disabled={!circle || isSaving}
                        aria-label={t(
                          "imageFileIndex.electricalSafety.downloadModal.removePoint",
                          {
                            defaultValue: "Remove {{label}}",
                            label: pointLabel,
                          },
                        )}
                        title={t(
                          "imageFileIndex.electricalSafety.downloadModal.removePoint",
                          {
                            defaultValue: "Remove {{label}}",
                            label: pointLabel,
                          },
                        )}
                      >
                        <Trash2 aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="reportEditModalFooter">
            <button
              type="button"
              className="ghostBtn"
              onClick={onClose}
              disabled={isSaving}
            >
              {t("editorBtnBox.cancelButton", {
                defaultValue: "Cancel",
              })}
            </button>
            <button
              type="button"
              className="primaryBtn"
              onClick={handleSave}
              disabled={!isReadyToSave || isSaving}
            >
              {isSaving
                ? t("imageFileIndex.electricalSafety.downloadModal.saving", {
                    defaultValue: "Saving...",
                  })
                : t("imageFileIndex.buttons.thermalSave", {
                    defaultValue: "Save Thermal Information",
                  })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
